import { Callout } from "@/components";
import { useNotification } from "@/hooks";
import {
    Accordion,
    Button,
    ColorField,
    Heading,
    Input,
    LoadingProgress,
    Modal,
    Select,
    Thumbnail,
    buildHtmlPreviewSrcDoc,
    colorValueToCss,
    defaultSolid,
    normalizeHex,
    parseCssGradient,
    solidFromValue,
} from "@/ui";
import { useMutation } from "@apollo/react-hooks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MUTATION_GET_IMAGEURL } from "../../../apollo/module-operations/file_upload";
import useMeta from "../../../base/hooks/useMeta";
import { notificationSettingOptions } from "../../../config/constant";
import { CUSTOMIZE_FIELDS, MODES_FILL } from "./constants/customizeFields";
import {
    DEFAULT_LOGO_SETTINGS,
    PRODUCT_LOGO_PLACEHOLDER,
} from "./constants/logos";
import { EMAIL_SKINS, HANDLER_ORDER_CONFIRM } from "./constants/skins";
import { CUSTOMIZATION_SKIN, SKIN_CUSTOMIZATION } from "./constants/themeDraft";
import {
    buildEmailThemeColorTokens,
    buildThemedEmailHtml,
    ensureBaseEmailHtml,
    getThemeTokenKeysForHandler,
    hasEmailThemeTemplate,
    isCustomizeFieldUsedInTokens,
} from "./utils/buildThemedEmail";
import { hasLogoOverrides, mergeLogoSettings } from "./utils/logoTokens";
import {
    clearThemeDraft,
    emptyThemeDraft,
    getThemeDraft,
    isBuiltinEmailSkinId,
    setThemeDraft,
} from "./utils/themeDraftStorage";
import {
    MOCK_PRISMIC_THEME,
    fetchStoreThemeTokens,
    resolveSkinTokens,
} from "./utils/themeTokens";
import { resolveIconUrlsForApply } from "./utils/uploadIcons";
import { resolveLogoSettingsForApply } from "./utils/uploadLogos";
import styles from "./ChooseEmailThemeModal.module.scss";

const APPLY_SKIN_MS = 600;
/** Coalesce ColorField drag updates before rebuilding preview HTML. */
const PREVIEW_DEBOUNCE_MS = 48;

/** Sidebar accordion: one panel open; default Theme. */
const SIDEBAR_ACCORDION_DEFAULT = "theme";

const MOCK_THEME_DETAIL_BY_REASON = {
    missing_endpoint: "This store has no Prismic API endpoint configured.",
    empty_theme: "Theme document or variables were not found in Prismic.",
    parse_empty: "Theme variables from Prismic could not be parsed.",
    fetch_error: "There was a connection or API error talking to Prismic.",
};

/**
 * DEMO: force mock palette + warning Callout (skip real Prismic).
 * Set to false / remove when done reviewing the fallback UI.
 */
const DEMO_FORCE_MOCK_THEME = false;
/** Which reason copy to show while DEMO_FORCE_MOCK_THEME is on. */
const DEMO_MOCK_REASON = "fetch_error";
/**
 * DEMO: temporary Prismic CDN override (null = use store.prismic_api_endpoint).
 */
const DEMO_PRISMIC_API_ENDPOINT = null;

const canRetryPrismicTheme = reason =>
    reason != null && reason !== "missing_endpoint";

const CheckIcon = () => (
    <svg
        aria-hidden
        className={styles.checkIcon}
        fill="none"
        height={18}
        viewBox="0 0 18 18"
        width={18}
    >
        <circle cx="9" cy="9" fill="currentColor" r="9" />
        <path
            d="M5.2 9.1l2.4 2.4 5.2-5.2"
            stroke="#fff"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.8"
        />
    </svg>
);

function cssToColorValue(css) {
    const raw = String(css || "").trim();
    if (!raw) return defaultSolid("#ffffff");
    if (/gradient\(/i.test(raw)) {
        return parseCssGradient(raw) || defaultSolid("#ffffff");
    }
    const hex = normalizeHex(raw);
    return defaultSolid(hex || "#ffffff");
}

function overridesToCssMap(overrides) {
    if (!overrides) return null;
    const out = {};
    for (const [key, value] of Object.entries(overrides)) {
        if (!value) continue;
        out[key] = colorValueToCss(value);
    }
    return Object.keys(out).length ? out : null;
}

/**
 * Master–detail modal: THEME + CUSTOMIZE (left) · live desktop preview (right).
 * Overrides merge on top of resolveSkinTokens; Apply writes themed HTML to the form.
 */
const ChooseEmailThemeModal = ({
    open,
    onOpenChange,
    onApply,
    handler = "order/confirm_notify",
    prismicApiEndpoint = null,
    storeLogo = "",
    storeLogoSmall = "",
    store = null,
}) => {
    const { user } = useMeta();
    const notify = useNotification();
    const [generatePresignedUrl] = useMutation(MUTATION_GET_IMAGEURL);
    const [phase, setPhase] = useState("loading"); // loading | ready | applying
    /** Base HTML chunk for this handler is in cache. */
    const [templateReady, setTemplateReady] = useState(false);
    const [selectedId, setSelectedId] = useState("brand");
    /** When null, preview follows the form `handler` until the user changes Email type. */
    const [previewHandlerOverride, setPreviewHandlerOverride] = useState(null);
    /** Staged color + logo draft for this store (localStorage). */
    const [draft, setDraft] = useState(null);
    /** Builtin skin to switch to after confirming discard of Customization. */
    const [pendingDiscardSkinId, setPendingDiscardSkinId] = useState(null);
    const [prismicTheme, setPrismicTheme] = useState(null);
    /** @type {['prismic' | 'mock', Function]} */
    const [themeSource, setThemeSource] = useState("mock");
    /** @type {[string | null, Function]} */
    const [themeFallbackReason, setThemeFallbackReason] = useState(null);
    const [openFieldKey, setOpenFieldKey] = useState(null);
    const [sidebarSection, setSidebarSection] = useState(
        SIDEBAR_ACCORDION_DEFAULT,
    );

    const previewHandler =
        previewHandlerOverride && hasEmailThemeTemplate(previewHandlerOverride)
            ? previewHandlerOverride
            : hasEmailThemeTemplate(handler)
            ? handler
            : HANDLER_ORDER_CONFIRM;

    const applyThemeResult = useCallback(result => {
        setPrismicTheme(result.tokens);
        setThemeSource(result.source);
        setThemeFallbackReason(
            result.source === "mock" ? result.reason || null : null,
        );
    }, []);

    const loadThemeFromPrismic = useCallback(async () => {
        const endpoint = DEMO_PRISMIC_API_ENDPOINT || prismicApiEndpoint;
        try {
            return await fetchStoreThemeTokens(endpoint);
        } catch {
            return {
                tokens: { ...MOCK_PRISMIC_THEME },
                source: "mock",
                reason: "fetch_error",
            };
        }
    }, [prismicApiEndpoint]);

    useEffect(() => {
        if (!open) {
            setPhase("loading");
            setTemplateReady(false);
            setSelectedId("brand");
            setPreviewHandlerOverride(null);
            setDraft(null);
            setPendingDiscardSkinId(null);
            setPrismicTheme(null);
            setThemeSource("mock");
            setThemeFallbackReason(null);
            setOpenFieldKey(null);
            setSidebarSection(SIDEBAR_ACCORDION_DEFAULT);
            return undefined;
        }

        let cancelled = false;
        setPhase("loading");
        setPendingDiscardSkinId(null);
        setOpenFieldKey(null);
        setThemeFallbackReason(null);
        setSidebarSection(SIDEBAR_ACCORDION_DEFAULT);
        setPreviewHandlerOverride(null);
        const existing = getThemeDraft(user?.accountId, user?.storeId);
        setDraft(existing);
        setSelectedId(existing ? SKIN_CUSTOMIZATION : "brand");

        if (DEMO_FORCE_MOCK_THEME) {
            const timerId = window.setTimeout(() => {
                if (cancelled) return;
                setPrismicTheme({ ...MOCK_PRISMIC_THEME });
                setThemeSource("mock");
                setThemeFallbackReason(DEMO_MOCK_REASON);
                setPhase("ready");
            }, 700);
            return () => {
                cancelled = true;
                window.clearTimeout(timerId);
            };
        }

        loadThemeFromPrismic().then(result => {
            if (cancelled) return;
            applyThemeResult(result);
            setPhase("ready");
        });

        return () => {
            cancelled = true;
        };
    }, [
        open,
        loadThemeFromPrismic,
        applyThemeResult,
        user?.accountId,
        user?.storeId,
    ]);

    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        setTemplateReady(false);
        ensureBaseEmailHtml(previewHandler)
            .then(() => {
                if (!cancelled) setTemplateReady(true);
            })
            .catch(err => {
                console.error(err);
            });
        return () => {
            cancelled = true;
        };
    }, [open, previewHandler]);

    /** Same full-screen loading path as opening the modal. */
    const handleRetryPrismicTheme = useCallback(async () => {
        if (phase !== "ready") return;

        setPhase("loading");
        setTemplateReady(false);
        setOpenFieldKey(null);
        setThemeFallbackReason(null);

        try {
            const templatePromise = ensureBaseEmailHtml(previewHandler).then(
                () => {
                    setTemplateReady(true);
                },
            );
            if (DEMO_FORCE_MOCK_THEME) {
                await Promise.all([
                    new Promise(resolve => {
                        window.setTimeout(resolve, 700);
                    }),
                    templatePromise,
                ]);
                setPrismicTheme({ ...MOCK_PRISMIC_THEME });
                setThemeSource("mock");
                setThemeFallbackReason(DEMO_MOCK_REASON);
            } else {
                const [result] = await Promise.all([
                    loadThemeFromPrismic(),
                    templatePromise,
                ]);
                applyThemeResult(result);
            }
        } finally {
            setPhase("ready");
        }
    }, [applyThemeResult, previewHandler, loadThemeFromPrismic, phase]);

    const theme = prismicTheme || MOCK_PRISMIC_THEME;

    const mockThemeDetail =
        themeFallbackReason && MOCK_THEME_DETAIL_BY_REASON[themeFallbackReason]
            ? MOCK_THEME_DETAIL_BY_REASON[themeFallbackReason]
            : null;

    const showPrismicRetry = canRetryPrismicTheme(themeFallbackReason);

    const isCustomization = selectedId === SKIN_CUSTOMIZATION;
    const activeSkinId = isCustomization
        ? isBuiltinEmailSkinId(draft?.baseSkinId)
            ? draft.baseSkinId
            : "brand"
        : selectedId;

    const baseTokens = useMemo(
        () => resolveSkinTokens(activeSkinId, theme),
        [activeSkinId, theme],
    );

    const skinOverrides = isCustomization ? draft?.overrides || {} : {};
    const logoSettings = useMemo(
        () => mergeLogoSettings(isCustomization ? draft?.logos : null),
        [draft?.logos, isCustomization],
    );
    const logoHasOverrides = hasLogoOverrides(
        isCustomization ? draft?.logos : null,
    );

    const templateOptions = useMemo(
        () =>
            notificationSettingOptions.filter(option =>
                hasEmailThemeTemplate(option.value),
            ),
        [],
    );

    const pendingDiscardSkinTitle =
        EMAIL_SKINS.find(skin => skin.id === pendingDiscardSkinId)?.title ||
        "this theme";

    /** Only show Customize fields used by this handler's base HTML. */
    const visibleCustomizeFields = useMemo(() => {
        if (!templateReady) return CUSTOMIZE_FIELDS;
        try {
            const tokenKeys = getThemeTokenKeysForHandler(previewHandler);
            if (!tokenKeys.size) return CUSTOMIZE_FIELDS;
            const filtered = CUSTOMIZE_FIELDS.filter(field =>
                isCustomizeFieldUsedInTokens(field.key, tokenKeys),
            );
            // Fail open: never leave the panel empty if matching glitches.
            return filtered.length ? filtered : CUSTOMIZE_FIELDS;
        } catch {
            return CUSTOMIZE_FIELDS;
        }
    }, [previewHandler, templateReady]);

    useEffect(() => {
        if (
            openFieldKey &&
            !visibleCustomizeFields.some(field => field.key === openFieldKey)
        ) {
            setOpenFieldKey(null);
        }
    }, [openFieldKey, visibleCustomizeFields]);

    const tokenOverridesCss = useMemo(
        () => overridesToCssMap(skinOverrides),
        [skinOverrides],
    );

    const skinList = useMemo(
        () => (draft ? [...EMAIL_SKINS, CUSTOMIZATION_SKIN] : EMAIL_SKINS),
        [draft],
    );

    const builtinSkinThumbs = useMemo(() => {
        if (!templateReady) return [];
        return EMAIL_SKINS.map(skin => {
            const html = buildThemedEmailHtml(skin.id, {
                handler: previewHandler,
                prismicTheme: theme,
                logoSettings: null,
                forPreview: true,
                store,
            });
            return {
                id: skin.id,
                srcDoc: buildHtmlPreviewSrcDoc(html),
            };
        });
    }, [previewHandler, store, templateReady, theme]);

    const skinThumbs = useMemo(() => {
        if (!draft) return builtinSkinThumbs;
        const customizationHtml = buildThemedEmailHtml(
            isBuiltinEmailSkinId(draft.baseSkinId) ? draft.baseSkinId : "brand",
            {
                handler: previewHandler,
                prismicTheme: theme,
                tokenOverrides: overridesToCssMap(draft.overrides),
                logoSettings: draft.logos,
                forPreview: true,
                store,
            },
        );
        return [
            ...builtinSkinThumbs,
            {
                id: SKIN_CUSTOMIZATION,
                srcDoc: buildHtmlPreviewSrcDoc(customizationHtml),
            },
        ];
    }, [builtinSkinThumbs, draft, previewHandler, store, theme]);

    const previewHtml = useMemo(() => {
        if (!templateReady) return "";
        return buildThemedEmailHtml(activeSkinId, {
            handler: previewHandler,
            prismicTheme: theme,
            tokenOverrides: tokenOverridesCss,
            logoSettings,
            forPreview: true,
            store,
        });
    }, [
        activeSkinId,
        logoSettings,
        previewHandler,
        store,
        templateReady,
        theme,
        tokenOverridesCss,
    ]);

    const previewSrcDoc = useMemo(
        () => buildHtmlPreviewSrcDoc(previewHtml),
        [previewHtml],
    );

    /** Debounced srcDoc — skin / email-type changes flush immediately. */
    const [bufferedSrcDoc, setBufferedSrcDoc] = useState("");
    const selectedIdForPreviewRef = useRef(selectedId);
    const previewHandlerForPreviewRef = useRef(previewHandler);
    const hasBufferedRef = useRef(false);

    useEffect(() => {
        if (!open) {
            hasBufferedRef.current = false;
            setBufferedSrcDoc("");
        }
    }, [open]);

    useEffect(() => {
        if (!previewSrcDoc) return undefined;
        const skinChanged =
            selectedIdForPreviewRef.current !== selectedId ||
            previewHandlerForPreviewRef.current !== previewHandler;
        selectedIdForPreviewRef.current = selectedId;
        previewHandlerForPreviewRef.current = previewHandler;
        if (skinChanged || !hasBufferedRef.current) {
            hasBufferedRef.current = true;
            setBufferedSrcDoc(previewSrcDoc);
            return undefined;
        }
        const timerId = window.setTimeout(() => {
            setBufferedSrcDoc(previewSrcDoc);
        }, PREVIEW_DEBOUNCE_MS);
        return () => window.clearTimeout(timerId);
    }, [previewSrcDoc, previewHandler, selectedId]);

    /**
     * Double-buffer: load into the hidden iframe, swap on load (no flash).
     * Scroll is copied from the visible frame at swap time.
     */
    const [activeSlot, setActiveSlot] = useState(0);
    const [slots, setSlots] = useState([
        { doc: "", key: 0 },
        { doc: "", key: 0 },
    ]);
    const activeSlotRef = useRef(0);
    const frame0Ref = useRef(null);
    const frame1Ref = useRef(null);
    const slotsRef = useRef(slots);
    const previewScrollRef = useRef(0);
    const loadGenRef = useRef(0);
    const pendingLoadRef = useRef({ slot: 0, gen: 0 });
    const skipScrollCaptureRef = useRef(false);

    useEffect(() => {
        activeSlotRef.current = activeSlot;
    }, [activeSlot]);

    useEffect(() => {
        slotsRef.current = slots;
    }, [slots]);

    useEffect(() => {
        previewScrollRef.current = 0;
        skipScrollCaptureRef.current = true;
    }, [previewHandler, selectedId]);

    useEffect(() => {
        if (!open) {
            setActiveSlot(0);
            setSlots([
                { doc: "", key: 0 },
                { doc: "", key: 0 },
            ]);
            previewScrollRef.current = 0;
            loadGenRef.current = 0;
        }
    }, [open]);

    const getFrame = useCallback(
        slot => (slot === 0 ? frame0Ref.current : frame1Ref.current),
        [],
    );

    const restoreScrollToFrame = useCallback(frame => {
        const doc = frame?.contentDocument;
        if (!doc) return;
        const y = previewScrollRef.current;
        const apply = () => {
            if (doc.documentElement) doc.documentElement.scrollTop = y;
            if (doc.body) doc.body.scrollTop = y;
        };
        apply();
        window.requestAnimationFrame(apply);
    }, []);

    useEffect(() => {
        if (!open || phase === "loading" || !bufferedSrcDoc) return;

        const active = activeSlotRef.current;
        const inactive = 1 - active;
        const hasActiveDoc = Boolean(slotsRef.current[active].doc);
        const targetSlot = hasActiveDoc ? inactive : active;

        if (
            hasActiveDoc &&
            targetSlot === inactive &&
            !skipScrollCaptureRef.current
        ) {
            const activeDoc = getFrame(active)?.contentDocument;
            if (activeDoc) {
                previewScrollRef.current =
                    activeDoc.documentElement?.scrollTop ||
                    activeDoc.body?.scrollTop ||
                    previewScrollRef.current;
            }
        }
        skipScrollCaptureRef.current = false;

        const gen = ++loadGenRef.current;
        pendingLoadRef.current = { slot: targetSlot, gen };

        setSlots(prev => {
            const next = [...prev];
            next[targetSlot] = { doc: bufferedSrcDoc, key: gen };
            return next;
        });
    }, [bufferedSrcDoc, getFrame, open, phase]);

    const handlePreviewLoad = useCallback(
        slot => {
            const pending = pendingLoadRef.current;
            if (pending.slot !== slot || pending.gen !== loadGenRef.current) {
                return;
            }
            restoreScrollToFrame(getFrame(slot));
            if (slot !== activeSlotRef.current) {
                setActiveSlot(slot);
            }
        },
        [getFrame, restoreScrollToFrame],
    );

    const selectedSkin = skinList.find(s => s.id === selectedId);
    const hasOverrides = Object.keys(skinOverrides).length > 0;

    const persistDraft = useCallback(
        updater => {
            setDraft(prev => {
                const baseSkinId = isBuiltinEmailSkinId(selectedId)
                    ? selectedId
                    : prev?.baseSkinId || "brand";
                const current = prev || emptyThemeDraft(baseSkinId);
                const next =
                    typeof updater === "function"
                        ? updater({ ...current, baseSkinId })
                        : updater;
                return (
                    setThemeDraft(user?.accountId, user?.storeId, {
                        ...next,
                        updatedAt: Date.now(),
                    }) || next
                );
            });
            if (selectedId !== SKIN_CUSTOMIZATION) {
                setSelectedId(SKIN_CUSTOMIZATION);
            }
        },
        [selectedId, user?.accountId, user?.storeId],
    );

    const setFieldOverride = useCallback(
        (key, colorValue, allowedModes = MODES_FILL) => {
            let next = colorValue;
            if (
                Array.isArray(allowedModes) &&
                !allowedModes.includes(colorValue?.mode)
            ) {
                next = defaultSolid(solidFromValue(colorValue));
            }
            persistDraft(current => ({
                ...current,
                overrides: {
                    ...(current.overrides || {}),
                    [key]: next,
                },
            }));
        },
        [persistDraft],
    );

    const handleReset = useCallback(() => {
        persistDraft(current => ({
            ...current,
            overrides: {},
        }));
    }, [persistDraft]);

    const handleResetLogo = useCallback(() => {
        persistDraft(current => ({
            ...current,
            logos: { ...DEFAULT_LOGO_SETTINGS },
        }));
    }, [persistDraft]);

    const setLogoField = useCallback(
        (key, value) => {
            persistDraft(current => ({
                ...current,
                logos: {
                    ...mergeLogoSettings(current.logos),
                    [key]: value,
                },
            }));
        },
        [persistDraft],
    );

    const handleSelectSkin = useCallback(
        skinId => {
            if (!skinId || skinId === selectedId || phase === "applying") {
                return;
            }
            if (
                selectedId === SKIN_CUSTOMIZATION &&
                isBuiltinEmailSkinId(skinId)
            ) {
                setPendingDiscardSkinId(skinId);
                return;
            }
            setSelectedId(skinId);
            setOpenFieldKey(null);
        },
        [phase, selectedId],
    );

    const handleStayOnCustomization = useCallback(() => {
        setPendingDiscardSkinId(null);
    }, []);

    const handleDiscardCustomization = useCallback(() => {
        const nextSkinId = isBuiltinEmailSkinId(pendingDiscardSkinId)
            ? pendingDiscardSkinId
            : "brand";
        clearThemeDraft(user?.accountId, user?.storeId);
        setDraft(null);
        setSelectedId(nextSkinId);
        setPendingDiscardSkinId(null);
        setOpenFieldKey(null);
    }, [pendingDiscardSkinId, user?.accountId, user?.storeId]);

    const handleClose = useCallback(() => {
        if (phase === "applying") return;
        onOpenChange(false);
    }, [onOpenChange, phase]);

    const handleApply = useCallback(async () => {
        if (!activeSkinId || phase === "applying") return;
        if (!user?.accountId || !user?.storeId) {
            notify(
                "Cannot upload theme assets: store context is missing.",
                "error",
            );
            return;
        }
        setPhase("applying");
        try {
            await new Promise(resolve => {
                window.setTimeout(resolve, APPLY_SKIN_MS);
            });
            const uploadCtx = {
                accountId: user.accountId,
                storeId: user.storeId,
                skinId: activeSkinId,
                generatePresignedUrl,
            };
            const logoSettingsForApply = await resolveLogoSettingsForApply(
                logoSettings,
                {
                    ...uploadCtx,
                    storeLogo,
                    storeLogoSmall,
                },
            );
            await ensureBaseEmailHtml(previewHandler);
            const colorTokens = buildEmailThemeColorTokens(activeSkinId, {
                prismicTheme: theme,
                tokenOverrides: tokenOverridesCss,
                logoSettings: logoSettingsForApply,
                forPreview: false,
            });
            const iconKeys = getThemeTokenKeysForHandler(previewHandler);
            const iconTokens = await resolveIconUrlsForApply(
                colorTokens,
                uploadCtx,
                iconKeys,
            );
            const html = buildThemedEmailHtml(activeSkinId, {
                handler: previewHandler,
                prismicTheme: theme,
                tokenOverrides: tokenOverridesCss,
                logoSettings: logoSettingsForApply,
                forPreview: false,
                iconTokens,
            });
            await Promise.resolve(
                onApply({
                    skinId: activeSkinId,
                    html,
                    handler: previewHandler,
                }),
            );
            setPhase("ready");
        } catch (err) {
            console.error(err);
            const message =
                err?.message &&
                /THEME|Themed email HTML|Theme apply/i.test(err.message)
                    ? err.message
                    : "Could not upload theme logos/icons. Theme was not applied.";
            notify(message, "error");
            setPhase("ready");
        }
    }, [
        activeSkinId,
        generatePresignedUrl,
        logoSettings,
        notify,
        onApply,
        phase,
        previewHandler,
        storeLogo,
        storeLogoSmall,
        theme,
        tokenOverridesCss,
        user?.accountId,
        user?.storeId,
    ]);

    const fieldValue = key => {
        if (skinOverrides[key]) return skinOverrides[key];
        return cssToColorValue(baseTokens[key]);
    };

    const headerLogoPreviewBg =
        tokenOverridesCss?.headerBackground ||
        baseTokens.headerBackground ||
        "";
    const footerLogoPreviewBg =
        tokenOverridesCss?.footerBackground ||
        baseTokens.footerBackground ||
        "";

    return (
        <>
            <Modal
                bodyScroll={false}
                className={styles.modal}
                description="Applies and saves the email type selected in Preview. Stay open to apply other types."
                footer={
                    <div className={styles.footer}>
                        <Button
                            disabled={phase === "applying"}
                            onClick={handleClose}
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                        <Button
                            disabled={phase !== "ready"}
                            onClick={handleApply}
                        >
                            Apply theme
                        </Button>
                    </div>
                }
                onOpenChange={next => {
                    if (!next) handleClose();
                }}
                open={open}
                size="lg"
                title="Choose email theme"
            >
                {phase === "loading" ? (
                    <div className={styles.loading}>
                        <LoadingProgress
                            className={styles.loadingProgress}
                            indeterminate
                            indicator="circular"
                            size="sm"
                            subtitle="Getting theme colors from Prismic…"
                            title="Loading themes"
                        />
                    </div>
                ) : (
                    <div
                        aria-busy={phase === "applying" || undefined}
                        className={styles.layout}
                    >
                        <aside className={styles.sidebar}>
                            <Accordion
                                className={styles.sidebarAccordion}
                                collapsible={false}
                                onValueChange={value => {
                                    if (value) setSidebarSection(value);
                                }}
                                size="small"
                                type="single"
                                value={sidebarSection}
                                variant="list"
                            >
                                <Accordion.Item
                                    className={styles.sidebarAccordionItem}
                                    value="theme"
                                >
                                    <div
                                        className={
                                            styles.sectionAccordionHeader
                                        }
                                    >
                                        <Accordion.Header
                                            className={
                                                styles.sectionAccordionHeading
                                            }
                                        >
                                            <Accordion.Trigger
                                                className={
                                                    styles.sidebarAccordionTrigger
                                                }
                                                hideIcon
                                            >
                                                Theme
                                            </Accordion.Trigger>
                                        </Accordion.Header>
                                        <Accordion.Header
                                            className={
                                                styles.sectionAccordionChevron
                                            }
                                        >
                                            <Accordion.Trigger
                                                aria-label="Toggle Theme"
                                                className={
                                                    styles.chevronTrigger
                                                }
                                            />
                                        </Accordion.Header>
                                    </div>
                                    <Accordion.Content
                                        className={
                                            styles.sidebarAccordionContent
                                        }
                                    >
                                        <div
                                            aria-label="Email theme skin"
                                            className={styles.themeList}
                                            role="radiogroup"
                                        >
                                            {skinList.map(skin => {
                                                const selected =
                                                    selectedId === skin.id;
                                                const thumb = skinThumbs.find(
                                                    t => t.id === skin.id,
                                                );
                                                return (
                                                    <button
                                                        aria-checked={selected}
                                                        className={
                                                            selected
                                                                ? styles.skinItemSelected
                                                                : styles.skinItem
                                                        }
                                                        disabled={
                                                            phase === "applying"
                                                        }
                                                        key={skin.id}
                                                        onClick={() => {
                                                            handleSelectSkin(
                                                                skin.id,
                                                            );
                                                        }}
                                                        role="radio"
                                                        type="button"
                                                    >
                                                        <div
                                                            aria-hidden
                                                            className={
                                                                styles.skinThumb
                                                            }
                                                        >
                                                            <div
                                                                className={
                                                                    styles.skinThumbScale
                                                                }
                                                            >
                                                                <iframe
                                                                    className={
                                                                        styles.skinThumbFrame
                                                                    }
                                                                    srcDoc={
                                                                        thumb?.srcDoc
                                                                    }
                                                                    tabIndex={
                                                                        -1
                                                                    }
                                                                    title=""
                                                                />
                                                            </div>
                                                        </div>
                                                        <span
                                                            className={
                                                                styles.skinText
                                                            }
                                                        >
                                                            <span
                                                                className={
                                                                    styles.skinTitle
                                                                }
                                                            >
                                                                {skin.title}
                                                            </span>
                                                            <span
                                                                className={
                                                                    styles.skinDesc
                                                                }
                                                            >
                                                                {
                                                                    skin.description
                                                                }
                                                            </span>
                                                        </span>
                                                        {selected ? (
                                                            <CheckIcon />
                                                        ) : (
                                                            <span
                                                                aria-hidden
                                                                className={
                                                                    styles.checkSpacer
                                                                }
                                                            />
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </Accordion.Content>
                                </Accordion.Item>

                                <Accordion.Item
                                    className={styles.sidebarAccordionItem}
                                    value="brand"
                                >
                                    <div
                                        className={
                                            styles.sectionAccordionHeader
                                        }
                                    >
                                        <Accordion.Header
                                            className={
                                                styles.sectionAccordionHeading
                                            }
                                        >
                                            <Accordion.Trigger
                                                className={
                                                    styles.sidebarAccordionTrigger
                                                }
                                                hideIcon
                                            >
                                                Customize{" "}
                                                {selectedSkin?.title || "Brand"}
                                            </Accordion.Trigger>
                                        </Accordion.Header>
                                        <button
                                            className={styles.resetBtn}
                                            disabled={
                                                !hasOverrides ||
                                                phase === "applying"
                                            }
                                            onClick={e => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleReset();
                                            }}
                                            type="button"
                                        >
                                            Reset to default
                                        </button>
                                        <Accordion.Header
                                            className={
                                                styles.sectionAccordionChevron
                                            }
                                        >
                                            <Accordion.Trigger
                                                aria-label="Toggle Customize Brand"
                                                className={
                                                    styles.chevronTrigger
                                                }
                                            />
                                        </Accordion.Header>
                                    </div>
                                    <Accordion.Content
                                        className={
                                            styles.sidebarAccordionContent
                                        }
                                    >
                                        <div className={styles.customizeList}>
                                            {visibleCustomizeFields.map(
                                                (field, index) => {
                                                    const prevGroup =
                                                        visibleCustomizeFields[
                                                            index - 1
                                                        ]?.group;
                                                    const showGroup =
                                                        field.group &&
                                                        field.group !==
                                                            prevGroup;
                                                    const isFieldOpen =
                                                        openFieldKey ===
                                                        field.key;
                                                    return (
                                                        <div
                                                            className={
                                                                styles.customizeItem
                                                            }
                                                            key={field.key}
                                                        >
                                                            {showGroup ? (
                                                                <p
                                                                    className={
                                                                        styles.customizeGroup
                                                                    }
                                                                >
                                                                    {
                                                                        field.group
                                                                    }
                                                                </p>
                                                            ) : null}
                                                            <ColorField
                                                                allowedModes={
                                                                    field.allowedModes
                                                                }
                                                                disabled={
                                                                    phase ===
                                                                    "applying"
                                                                }
                                                                label={
                                                                    field.label
                                                                }
                                                                onOpenChange={next =>
                                                                    setOpenFieldKey(
                                                                        next
                                                                            ? field.key
                                                                            : null,
                                                                    )
                                                                }
                                                                onValueChange={next =>
                                                                    setFieldOverride(
                                                                        field.key,
                                                                        next,
                                                                        field.allowedModes,
                                                                    )
                                                                }
                                                                open={
                                                                    isFieldOpen
                                                                }
                                                                value={fieldValue(
                                                                    field.key,
                                                                )}
                                                                variant="accordion"
                                                            />
                                                        </div>
                                                    );
                                                },
                                            )}
                                        </div>
                                    </Accordion.Content>
                                </Accordion.Item>

                                <Accordion.Item
                                    className={styles.sidebarAccordionItem}
                                    value="logo"
                                >
                                    <div
                                        className={
                                            styles.sectionAccordionHeader
                                        }
                                    >
                                        <Accordion.Header
                                            className={
                                                styles.sectionAccordionHeading
                                            }
                                        >
                                            <Accordion.Trigger
                                                className={
                                                    styles.sidebarAccordionTrigger
                                                }
                                                hideIcon
                                            >
                                                Customize Logo
                                            </Accordion.Trigger>
                                        </Accordion.Header>
                                        <button
                                            className={styles.resetBtn}
                                            disabled={
                                                !logoHasOverrides ||
                                                phase === "applying"
                                            }
                                            onClick={e => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleResetLogo();
                                            }}
                                            type="button"
                                        >
                                            Reset to default
                                        </button>
                                        <Accordion.Header
                                            className={
                                                styles.sectionAccordionChevron
                                            }
                                        >
                                            <Accordion.Trigger
                                                aria-label="Toggle Customize Logo"
                                                className={
                                                    styles.chevronTrigger
                                                }
                                            />
                                        </Accordion.Header>
                                    </div>
                                    <Accordion.Content
                                        className={
                                            styles.sidebarAccordionContent
                                        }
                                    >
                                        <div
                                            className={styles.logoCustomizeList}
                                        >
                                            <div
                                                className={styles.logoGroupRow}
                                            >
                                                <p
                                                    className={
                                                        styles.customizeGroup
                                                    }
                                                >
                                                    Logo header
                                                </p>
                                                <span
                                                    className={
                                                        styles.logoGroupHint
                                                    }
                                                >
                                                    Empty uses store logo (PNG
                                                    on Apply)
                                                </span>
                                            </div>
                                            <div
                                                className={styles.logoEditorRow}
                                            >
                                                <div
                                                    className={
                                                        styles.logoPreviewSlot
                                                    }
                                                    style={
                                                        headerLogoPreviewBg
                                                            ? {
                                                                  background:
                                                                      headerLogoPreviewBg,
                                                              }
                                                            : undefined
                                                    }
                                                >
                                                    <Thumbnail
                                                        alt=""
                                                        size="lg"
                                                        src={
                                                            logoSettings.headerUrl.trim() ||
                                                            PRODUCT_LOGO_PLACEHOLDER
                                                        }
                                                        variant="product-image"
                                                    />
                                                </div>
                                                <div
                                                    className={
                                                        styles.logoFieldsColumn
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            styles.logoUrlField
                                                        }
                                                    >
                                                        <Input
                                                            density="compact"
                                                            disabled={
                                                                phase ===
                                                                "applying"
                                                            }
                                                            fullWidth
                                                            label="URL"
                                                            labelBehavior="inline"
                                                            onChange={e =>
                                                                setLogoField(
                                                                    "headerUrl",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            value={
                                                                logoSettings.headerUrl
                                                            }
                                                        />
                                                    </div>
                                                    <div
                                                        className={
                                                            styles.logoWidthField
                                                        }
                                                    >
                                                        <Input
                                                            density="compact"
                                                            disabled={
                                                                phase ===
                                                                "applying"
                                                            }
                                                            fullWidth
                                                            label="Width"
                                                            labelBehavior="inline"
                                                            onChange={e =>
                                                                setLogoField(
                                                                    "headerWidth",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            suffix="px"
                                                            value={
                                                                logoSettings.headerWidth
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <div
                                                className={styles.logoGroupRow}
                                            >
                                                <p
                                                    className={
                                                        styles.customizeGroup
                                                    }
                                                >
                                                    Logo footer
                                                </p>
                                                <span
                                                    className={
                                                        styles.logoGroupHint
                                                    }
                                                >
                                                    Empty uses store small logo
                                                    (PNG on Apply)
                                                </span>
                                            </div>
                                            <div
                                                className={styles.logoEditorRow}
                                            >
                                                <div
                                                    className={
                                                        styles.logoPreviewSlot
                                                    }
                                                    style={
                                                        footerLogoPreviewBg
                                                            ? {
                                                                  background:
                                                                      footerLogoPreviewBg,
                                                              }
                                                            : undefined
                                                    }
                                                >
                                                    <Thumbnail
                                                        alt=""
                                                        size="lg"
                                                        src={
                                                            logoSettings.footerUrl.trim() ||
                                                            PRODUCT_LOGO_PLACEHOLDER
                                                        }
                                                        variant="product-image"
                                                    />
                                                </div>
                                                <div
                                                    className={
                                                        styles.logoFieldsColumn
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            styles.logoUrlField
                                                        }
                                                    >
                                                        <Input
                                                            density="compact"
                                                            disabled={
                                                                phase ===
                                                                "applying"
                                                            }
                                                            fullWidth
                                                            label="URL"
                                                            labelBehavior="inline"
                                                            onChange={e =>
                                                                setLogoField(
                                                                    "footerUrl",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            value={
                                                                logoSettings.footerUrl
                                                            }
                                                        />
                                                    </div>
                                                    <div
                                                        className={
                                                            styles.logoWidthField
                                                        }
                                                    >
                                                        <Input
                                                            density="compact"
                                                            disabled={
                                                                phase ===
                                                                "applying"
                                                            }
                                                            fullWidth
                                                            label="Width"
                                                            labelBehavior="inline"
                                                            onChange={e =>
                                                                setLogoField(
                                                                    "footerWidth",
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                            suffix="px"
                                                            value={
                                                                logoSettings.footerWidth
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Accordion.Content>
                                </Accordion.Item>
                            </Accordion>
                        </aside>

                        <section className={styles.previewPane}>
                            <div className={styles.previewHeader}>
                                <div className={styles.previewHeading}>
                                    <Heading
                                        className={styles.previewTitle}
                                        level={5}
                                    >
                                        Preview
                                    </Heading>
                                    <span className={styles.skinBadge}>
                                        {selectedSkin?.title || "Brand"}
                                    </span>
                                </div>
                                <Select
                                    align="end"
                                    appearance="pill"
                                    className={styles.previewTypeSelect}
                                    disabled={phase === "applying"}
                                    label="Email type"
                                    leading="email-template"
                                    leadingTooltip="Switch the email type to preview this theme on another notification. Apply saves only the type shown here."
                                    onValueChange={setPreviewHandlerOverride}
                                    options={templateOptions}
                                    value={previewHandler}
                                />
                            </div>
                            {themeSource === "mock" ? (
                                <div className={styles.previewThemeCalloutWrap}>
                                    <Callout role="status" variant="warning">
                                        <p
                                            className={
                                                styles.previewThemeCalloutText
                                            }
                                        >
                                            Couldn&apos;t load theme colors from
                                            Prismic. Using a default mock
                                            palette for this preview.
                                            {mockThemeDetail
                                                ? ` ${mockThemeDetail}`
                                                : null}
                                            {showPrismicRetry ? (
                                                <>
                                                    {" "}
                                                    <button
                                                        className={
                                                            styles.previewThemeRetry
                                                        }
                                                        disabled={
                                                            phase === "applying"
                                                        }
                                                        onClick={
                                                            handleRetryPrismicTheme
                                                        }
                                                        type="button"
                                                    >
                                                        Retry
                                                    </button>
                                                </>
                                            ) : null}
                                        </p>
                                    </Callout>
                                </div>
                            ) : null}
                            <div className={styles.previewScroll}>
                                <div className={styles.previewStack}>
                                    {slots[0].doc ? (
                                        <iframe
                                            aria-hidden={activeSlot !== 0}
                                            className={styles.previewFrame}
                                            data-active={activeSlot === 0}
                                            key={`preview-0-${slots[0].key}`}
                                            onLoad={() => handlePreviewLoad(0)}
                                            ref={frame0Ref}
                                            srcDoc={slots[0].doc}
                                            title={`${
                                                selectedSkin?.title || "Email"
                                            } theme preview`}
                                        />
                                    ) : null}
                                    {slots[1].doc ? (
                                        <iframe
                                            aria-hidden={activeSlot !== 1}
                                            className={styles.previewFrame}
                                            data-active={activeSlot === 1}
                                            key={`preview-1-${slots[1].key}`}
                                            onLoad={() => handlePreviewLoad(1)}
                                            ref={frame1Ref}
                                            srcDoc={slots[1].doc}
                                            title={`${
                                                selectedSkin?.title || "Email"
                                            } theme preview`}
                                        />
                                    ) : null}
                                </div>
                            </div>
                        </section>

                        {phase === "applying" ? (
                            <div
                                aria-live="polite"
                                className={styles.applyingOverlay}
                                role="status"
                            >
                                <LoadingProgress
                                    indeterminate
                                    indicator="circular"
                                    size="sm"
                                    title="Applying"
                                />
                                <p className={styles.applyingHint}>
                                    Applying theme to this template…
                                </p>
                            </div>
                        ) : null}
                    </div>
                )}
            </Modal>
            <Modal
                description={`Switching to ${pendingDiscardSkinTitle} will discard your staged color and logo changes for this store.`}
                footer={
                    <div className={styles.footer}>
                        <Button
                            onClick={handleDiscardCustomization}
                            variant="secondary"
                        >
                            Discard
                        </Button>
                        <Button onClick={handleStayOnCustomization}>
                            Stay
                        </Button>
                    </div>
                }
                onOpenChange={next => {
                    if (!next) handleStayOnCustomization();
                }}
                open={Boolean(pendingDiscardSkinId)}
                title="Discard customization?"
            >
                {null}
            </Modal>
        </>
    );
};

export default ChooseEmailThemeModal;
